import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavService } from './nav.service';
import { Title } from '@angular/platform-browser';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Program } from '../shared/program/model/program';
import { Principal } from '../shared';
import { ToolService } from '../shared/tool/service/tool.service';
import { HttpResponse } from '@angular/common/http';
import { Tool, ToolLink } from '../shared/tool/model/tool.model';
import { LoginService } from '../shared/login/login.service';
import { HELP_NAVIGATION_ASK_FOR_SUPPORT, HELP_NAVIGATION_BAR_ABOUT_BMS, VERSION, ViewEnum } from '../app.constants';
import { HelpService } from '../shared/service/help.service';
import { ADD_PROGRAM_PERMISSION, FIELDMAP_MANAGER_PERMISSIONS, SITE_ADMIN_PERMISSIONS } from '../shared/auth/permissions';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NavbarMessageEvent } from '../shared/model/navbar-message.event';
import { ProgramService } from '../shared/program/service/program.service';
import { ProgramUsageService } from '../shared/service/program-usage.service';
import { CropParameterService } from '../shared/crop-parameter/service/crop-parameter.service';
import { Location, PopStateEvent } from '@angular/common';
import { ReleaseNotesService } from '../release-notes/release-notes.service';
import { LoginContext } from '../shared/login/login-context';
import { ParamContext } from '../shared/service/param.context';
import { AlertService } from '../shared/alert/alert.service';
import { MatSidenav } from '@angular/material/sidenav';
import { getUrl } from '../shared/util/dev-util';
import { GermplasmDetailsPopupService } from '../germplasm-details/germplasm-details-popup.service';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { SubscriptionLike } from 'rxjs';
import { JhiLanguageService } from '../shared/core-utils';

@Component({
    selector: 'jhi-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: [
        'navbar.scss'
    ],
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {

    SITE_ADMIN_PERMISSIONS = SITE_ADMIN_PERMISSIONS;
    ADD_PROGRAM_PERMISSION = ADD_PROGRAM_PERMISSION;
    FIELDMAP_MANAGER_PERMISSIONS = FIELDMAP_MANAGER_PERMISSIONS;

    leftSidenavOpen = true;

    version: string;
    program: Program;
    user: any;
    toolLinkSelected: string;

    aboutBMSHelpLink: string;
    askForSupportHelpLink: string;

    subscriptionManager = new SubscriptionsManager();

    @ViewChild('sideNav') sideNav: MatSidenav;

    treeControl = new FlatTreeControl<FlatNode>((node) => node.level, (node) => node.expandable);
    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable, (node) => node.children
    );
    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    private readonly SESSION_STORAGE_QUERY_PARAM_KEY = 'bms.queryParams';
    private locationSubscribscriptionLike: SubscriptionLike;

    constructor(
        private navService: NavService,
        private principal: Principal,
        private toolService: ToolService,
        private alertService: AlertService,
        private loginService: LoginService,
        private helpService: HelpService,
        private programService: ProgramService,
        private programUsageService: ProgramUsageService,
        private cropParameterService: CropParameterService,
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        private readonly title: Title,
        private releaseNotesService: ReleaseNotesService,
        private loginContext: LoginContext,
        private paramContext: ParamContext,
        private languageService: JhiLanguageService,
        private germplasmDetailsPopupService: GermplasmDetailsPopupService,
    ) {
        this.version = '';
        // Append a ".0" in BMS version if none is found. The .0 gets truncated from workbench.properties to webpack.common version
        if (VERSION) {
            this.version = VERSION.includes('.') ?  `BMS ${VERSION}` : `BMS ${VERSION}.0`;
        }

        // Get about bms help link url
        this.getHelpLink(this.aboutBMSHelpLink, HELP_NAVIGATION_BAR_ABOUT_BMS)
            .then((response) => this.aboutBMSHelpLink = response);
        // Get ask for support link url
        this.getHelpLink(this.askForSupportHelpLink, HELP_NAVIGATION_ASK_FOR_SUPPORT)
            .then((response) => this.askForSupportHelpLink = response);

        this.locationSubscribscriptionLike = this.location.subscribe((popStateEvent) => {
            this.onPopStateEvent(popStateEvent);
        });
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
        if (this.locationSubscribscriptionLike) {
            this.locationSubscribscriptionLike.unsubscribe();
        }
    }

    hasChild = (_: number, node: any) => node.expandable;

    _transformer(node: Node, level: number) {
        return {
            expandable: !!node.children && node.children.length > 0,
            nameTranslationKey: node.nameTranslationKey,
            name: node.name,
            level,
            link: node.link,
            parent: node.parent
        };
    }

    async ngOnInit() {
        this.paramContext.readParams();
        this.user = await this.principal.identity();
        if (this.loginContext.recentlyLogin) {
            this.myPrograms();
            this.subscriptionManager.add = this.releaseNotesService.showReleaseNotes(this.user.id).subscribe(
                (res: HttpResponse<boolean>) => {
                    if (res.body) {
                        this.router.navigate(['/', { outlets: { popup: 'release-notes-popup' }, }], {
                            replaceUrl: false,
                            skipLocationChange: true,
                            queryParamsHandling: 'merge'
                        });
                        sessionStorage.removeItem(this.SESSION_STORAGE_QUERY_PARAM_KEY);
                    }
                }
            );
            this.loginContext.recentlyLogin = false;
        } else {
            this.restoreRoute();
        }
    }

    ngAfterViewInit() {
        this.navService.sideNav = this.sideNav;
    }

    async openTool(toolParams: { url: string,
        toolName: string,
        redirectToCrossesAndSelectionsTab?: boolean,
        viewData?: any
    }) {
        let authParams = '';
        const cropName = this.program ? this.program.crop : null;
        const programUUID = this.program ? this.program.uniqueID : null;
        const selectedProjectId = this.program ? this.program.id : null;

        const hasParams = toolParams.url.includes('?');

        authParams += (hasParams ? '&' : '?') + 'cropName=' + cropName
                + '&programUUID=' + programUUID
                + '&selectedProjectId=' + selectedProjectId
                + '&loggedInUserId=' + this.user.id;
        if (toolParams.redirectToCrossesAndSelectionsTab) {
            authParams += '&redirectToCrossesAndSelectionsTab=' + toolParams.redirectToCrossesAndSelectionsTab;
        }

        authParams += '&restartApplication';

        this.toolLinkSelected = toolParams.url;

        let queryParams: Params = {
            cropName: cropName,
            programUUID: programUUID,
            selectedProjectId: selectedProjectId,
            loggedInUserId: this.user.id,
            toolUrl: toolParams.url,
            studyId: toolParams.viewData?.studyId,
        };

        if (toolParams.url === ViewEnum.STUDY_EDITOR) {
            // Always force to reload Study Editor.
            this.router.navigate([''], { skipLocationChange: true }).then(() => {
                this.router.navigate(['', ViewEnum.STUDY_EDITOR, toolParams.viewData?.studyId], { queryParams: queryParams, onSameUrlNavigation: 'reload' });
            });
        } else if (toolParams.url === ViewEnum.CREATE_STUDY) {
            this.router.navigate(['', ViewEnum.CREATE_STUDY], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.STUDY_MANAGER) {
            this.router.navigate(['', ViewEnum.STUDY_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.GERMPLASM_LIST_MANAGER) {
            queryParams = {
                ...queryParams,
                listId: toolParams.viewData?.listId,
                listName: toolParams.viewData?.listName,
            };
            this.router.navigate(['', ViewEnum.GERMPLASM_LIST_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.GERMPLASM_MANAGER) {
            this.router.navigate(['', ViewEnum.GERMPLASM_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.PROGRAM_SETTING_MANAGER) {
            this.router.navigate(['', ViewEnum.PROGRAM_SETTING_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.CROP_SETTING_MANAGER) {
            this.router.navigate(['', ViewEnum.CROP_SETTING_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.ABOUT) {
            this.router.navigate(['', ViewEnum.ABOUT], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.SINGLE_SITE_ANALYSIS) {
            this.router.navigate(['', ViewEnum.SINGLE_SITE_ANALYSIS], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.MULTI_SITE_ANALYSIS) {
            this.router.navigate(['', ViewEnum.MULTI_SITE_ANALYSIS], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.SAMPLE_MANAGER) {
            this.router.navigate(['', ViewEnum.SAMPLE_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.SITE_ADMIN) {
            this.router.navigate(['', ViewEnum.SITE_ADMIN], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.FIELD_MANAGER) {
            this.router.navigate(['', ViewEnum.FIELD_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.INVENTORY_MANAGER) {
            this.router.navigate(['', ViewEnum.INVENTORY_MANAGER], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.MULTI_TRAIT_QUERY) {
            this.router.navigate(['', ViewEnum.MULTI_TRAIT_QUERY], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.HEAD_TO_HEAD_QUERY) {
            this.router.navigate(['', ViewEnum.HEAD_TO_HEAD_QUERY], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.BRAPI_SYNC) {
            this.router.navigate(['', ViewEnum.BRAPI_SYNC], { queryParams: queryParams });
        } else if (toolParams.url === ViewEnum.ONTOLOGY_MANAGER) {
            this.router.navigate(['', ViewEnum.ONTOLOGY_MANAGER], { queryParams: queryParams });
        } else {
            this.router.navigate(['']).then(() => {
                this.router.navigate(['', 'default', getUrl(toolParams.url) + authParams], { onSameUrlNavigation: 'reload' });
            });
        }

        // this.router.navigate(['.'], {
        //     queryParams: {
        //         cropName,
        //         programUUID,
        //         toolUrl: toolParams.url,
        //         toolName: toolParams.toolName
        //     }
        // }).then(() => {
        //     this.title.setTitle(this.translateService.instant('global.title') + ': ' + toolParams.toolName)
        // });


    }

    myPrograms() {
        this.program = null;
        this.toolLinkSelected = null;
        this.router.navigate(['', ViewEnum.PROGRAMS]);

    }

    siteAdmin() {
        this.program = null;
        this.openTool({ url: ViewEnum.SITE_ADMIN, toolName: 'Site Admin' });
    }

    fieldMapManager() {
        this.program = null;
        this.openTool({ url: ViewEnum.FIELD_MANAGER, toolName: 'Fieldmap Manager' });
    }

    about() {
        this.openTool({ url: ViewEnum.ABOUT, toolName:'About' });
    }

    isSideNavAvailable() {
        return Boolean(this.program);
    }

    @HostListener('window:message', ['$event'])
    async onMessage(event: {data: NavbarMessageEvent}) {
        if (!event.data) {
            return;
        }

        if (event.data?.viewData?.type === 'germplasm-details-modal') {
            // Message received from an iframe to load germplasm details.
            this.germplasmDetailsPopupService.open(event.data?.viewData?.gid);
            return;
        }

        if (event.data.programSelected) {
            const program = event.data.programSelected;
            try {
                await this.programUsageService.save(program.crop, program.uniqueID).toPromise();
                await this.programUsageService.setContextProgram(program.uniqueID).toPromise();
                await this.getTools(program);

                // Open program with specific tool (e.g a particular study in manage studies)
                if (event.data.toolSelectedUrl) {
                    this.openTool({ url: event.data.toolSelectedUrl, toolName: event.data.toolSelectedName, viewData: event.data.viewData });
                    this.expandParent();
                } else {
                    const firstNode = this.treeControl.dataNodes[0];
                    this.treeControl.expand(firstNode);
                    const descendant = this.treeControl.getDescendants(firstNode)[0];
                    this.openTool({ url: descendant.link, toolName:descendant.name, viewData: event.data.viewData });
                }
            } catch (error) {
            }
        } else if (event.data.programUpdated && this.program) {
            this.program.name = event.data.programUpdated.name;
        } else if (event.data.programDeleted) {
            this.myPrograms();
        } else if (event.data.toolSelectedUrl) {
            this.openTool({ url:event.data.toolSelectedUrl, toolName: event.data.toolSelectedName, redirectToCrossesAndSelectionsTab: event.data.redirectToCrossesAndSelectionsTab, viewData: event.data.viewData });
            this.expandParent();
        } else if (event.data.userProfileChanged) {
            this.user = await this.principal.identity(true);
        } else if (event.data.updateContentMargins && this.leftSidenavOpen) {
            window.dispatchEvent(new Event('resize'));
        }
    }

    private toNode(tool: Tool): Node {
        const node: Node = {
            name: tool.name,
            nameTranslationKey: tool.nameTranslationKey
        };
        node['children'] = tool.children.map((child: ToolLink) => {
            return {
                name: child.name,
                nameTranslationKey: child.nameTranslationKey,
                link: child.link,
                parent: node
            };
        });

        return node;
    }

    logout() {
        this.loginService.logout();
    }

    private async getHelpLink(helpLink: string, key: string): Promise<string> {
        if (!helpLink || !helpLink.length) {
            return await this.helpService.getHelpLink(key).toPromise().then((response) => {
                return response.body;
            });
        }
    }

    private expandParent() {
        if (!this.treeControl.dataNodes || !this.treeControl.dataNodes.length) {
            return;
        }
        // Find node by selected tool name
        const selectedNode: FlatNode[] = this.treeControl.dataNodes.filter((node) => this.toolLinkSelected.startsWith(node.link));
        if (selectedNode.length === 1) {
            // Find parent node
            const parentNode: FlatNode[] = this.treeControl.dataNodes.filter((node) => node.name === selectedNode[0].parent.name);
            if (parentNode.length === 1) {
                this.treeControl.expand(parentNode[0]);
            }
        }
    }

    getTools(program) {
        return this.toolService.getTools(program.crop, program.uniqueID)
            .toPromise()
            .then((res: HttpResponse<Tool[]>) => {
                if (!(res.body && res.body.length)) {
                    this.alertService.error('error.no.tool.available');
                    return;
                }

                this.program = program;
                this.dataSource.data = res.body.map((response: Tool) => this.toNode(response));
            });
    }

    openMyProfile() {
        this.router.navigate(['', { outlets: { popup: 'user-profile-update' }, }], {
            replaceUrl: false,
            skipLocationChange: true,
            queryParamsHandling: 'merge'
        });
    }

    addProgram() {
        this.router.navigate(['/', { outlets: { popup: 'create-program' }, }], {
            replaceUrl: false,
            skipLocationChange: true
        });
    }

    @HostListener('window:beforeunload')
    private beforeunload() {
        this.persistQueryParams();
    }

    private persistQueryParams() {
        const queryIndex = this.router.url.lastIndexOf('?');
        if (queryIndex >= 0) {
            sessionStorage.setItem(this.SESSION_STORAGE_QUERY_PARAM_KEY, this.router.url.substring(queryIndex));
        }
    }

    /**
     * Restores route after login or page reload
     */
    private async restoreRoute() {
        let programUUID = this.route.snapshot.queryParams.programUUID;
        let cropName = this.route.snapshot.queryParams.cropName;
        let toolUrl = this.route.snapshot.queryParams.toolUrl;
        let toolName = this.route.snapshot.queryParams.toolName;
        let studyId = this.route.snapshot.queryParams.studyId;
        let listId = this.route.snapshot.queryParams.list_id;
        let listName = this.route.snapshot.queryParams.list_name;


        const hasQueryParams = () => {
            return (programUUID && cropName) || toolUrl;
        };

        const sessionQueryParams = sessionStorage.getItem(this.SESSION_STORAGE_QUERY_PARAM_KEY);
        sessionStorage.removeItem(this.SESSION_STORAGE_QUERY_PARAM_KEY);

        if (sessionQueryParams && !hasQueryParams()) {
            await this.router.navigateByUrl(sessionQueryParams);

            programUUID = this.route.snapshot.queryParams.programUUID;
            cropName = this.route.snapshot.queryParams.cropName;
            toolUrl = this.route.snapshot.queryParams.toolUrl;
            toolName = this.route.snapshot.queryParams.toolName;
        }

        if (!hasQueryParams()) {
            return;
        }

        let program: Program;
        if (cropName && programUUID) {
            program = await this.programService.getProgramByProgramUUID(cropName, programUUID).toPromise().then((resp) => resp.body);
        }
        const message: NavbarMessageEvent = {};
        if (program) {
            message.programSelected = program;
        }
        if (toolUrl) {
            message.toolSelectedUrl = toolUrl;
            message.toolSelectedName = toolName;
        }
        if (studyId) {
            message.viewData = { studyId: studyId };
        }
        if (listId && listName) {
            message.toolSelectedName = 'List: ' + listName,
            message.viewData = {
                listId: listId,
                listName: listName
            };
        }

        if (Object.keys(message).length) {
            this.onMessage({ data: message });
        }
    }

    /**
     * This (specifically, calling onMessage) will override native iframe back/forth functionality.
     * - Fixes Firefox back button, which won't reload iframe contents, though other browsers do (1).
     * - Also fixes back and forward to site admin in all browsers
     * - Fixes back issue to brapi-sync in (1)
     * - in some browsers (1) it will change iframe location twice, causing a double render of the component,
     * but it's almost unnoticeable (except in slow servers).
     *
     * (1) - Some browsers iframe history seem to work correctly (e.g. Chrome, Brave)
     */
    private async onPopStateEvent(popStateEvent: PopStateEvent) {
        if (popStateEvent.type === 'popstate') {
            const urlTree = this.router.parseUrl(popStateEvent.url);

            const programUUID = urlTree.queryParams.programUUID;
            const cropName = urlTree.queryParams.cropName;
            const toolUrl = urlTree.queryParams.toolUrl;
            const toolName = urlTree.queryParams.toolName;

            if (toolUrl) {
                this.toolLinkSelected = toolUrl;
            }

            if (!toolUrl) {
                this.myPrograms();
                return;
            }

            let program: Program;
            if ((cropName && programUUID)) {
                if (this.program && this.program.uniqueID === programUUID) {
                    program = this.program;
                } else {
                    program = await this.programService.getProgramByProgramUUID(cropName, programUUID).toPromise().then((resp) => resp.body);
                }
            } else {
                this.program = null;
            }
            const message: NavbarMessageEvent = {};
            if (program) {
                message.programSelected = program;
            }
            if (toolUrl) {
                message.toolSelectedUrl = toolUrl;
                message.toolSelectedName = toolName;
            }
            if (Object.keys(message).length) {
                this.onMessage({ data: message });
            }
        }
    }

    toggleLeftSidenav() {
        this.leftSidenavOpen = !this.leftSidenavOpen;
    }
}

interface Node {
    name: string;
    nameTranslationKey?: string;
    children?: Node[];
    link?: string;
    parent?: Node;
}

interface FlatNode {
    expandable: boolean;
    nameTranslationKey?: string;
    name: string;
    level: number;
    link?: string;
    parent?: Node;
}
